import type { DayRoute } from '../api/types';
import { Icon, type IconName } from '../components/icons';
import { LazyMapView as MapView } from '../components/LazyMapView';
import type { MapMarker } from '../components/MapView';
import { GoogleMapsAttribution } from '../places/GoogleAttribution';
import { fmtTime, travelLabel } from './format';

const hintIcons: Record<string, IconName> = { LATE_ARRIVAL: 'warning', TIGHT_BUFFER: 'clock', DETOUR: 'route', MISSING_LOCATION: 'help' };

// RoutePanel: Tagesroute in fester Terminreihenfolge mit Fahrzeiten, Puffern und Hinweisen.
// Routing empfiehlt nur – Termine werden nie automatisch verschoben.
export function RoutePanel({ route, showMap = true }: { route: DayRoute; showMap?: boolean }) {
  const markers: MapMarker[] = [];
  const path: [number, number][] = [];
  if (route.start.latitude != null && route.start.longitude != null && route.start.type !== 'FIRST_APPOINTMENT') {
    markers.push({ id: 'start', lat: route.start.latitude, lng: route.start.longitude, kind: 'start', label: 'S', title: route.start.label });
    path.push([route.start.latitude, route.start.longitude]);
  }
  route.stops.forEach((s, i) => {
    if (s.latitude == null || s.longitude == null) return;
    markers.push({ id: s.appointment_id, lat: s.latitude, lng: s.longitude, kind: 'stop', label: String(i + 1), title: `${fmtTime(s.start_at)} ${s.lead_name ?? ''}` });
    path.push([s.latitude, s.longitude]);
  });
  const hintsFor = (id: string) => route.hints.filter((h) => h.stop_id === id);
  return (
    <section className="card route-panel" aria-label="Tagesroute">
      <div className="section-head">
        <h2>Meine Route</h2>
        {route.stops.length > 0 && (
          <p className="muted">
            {route.stops.length} Termine · Fahrzeit {travelLabel(route.total_travel_minutes) || '0 Min.'} · {route.total_distance_km.toLocaleString('de-DE')} km
            {route.estimated && ' (geschätzt)'}
          </p>
        )}
      </div>
      {route.stops.length === 0 ? (
        <p className="muted">Keine Termine mit Adresse an diesem Tag.</p>
      ) : (
        <>
          {showMap && markers.length > 0 && <MapView markers={markers} path={path} ariaLabel="Karte der Tagesroute" className="map-route-view" />}
          <ol className="route-list">
            {route.start.type !== 'FIRST_APPOINTMENT' && (
              <li className="route-start">
                <span className="route-time">{route.departure_at ? fmtTime(route.departure_at) : '–'}</span>
                <span>
                  <strong>Start: {route.start.label}</strong>
                  {route.departure_at && <span className="muted block">Abfahrt spätestens</span>}
                </span>
              </li>
            )}
            {route.stops.map((s, i) => (
              <li key={s.appointment_id}>
                {s.travel_minutes != null && (
                  <p className="route-leg muted">
                    Fahrzeit: {travelLabel(s.travel_minutes)}
                    {s.distance_km != null && ` · ${s.distance_km.toLocaleString('de-DE')} km`}
                    {s.slack_minutes != null && s.slack_minutes >= 0 && ` · Reserve ${travelLabel(s.slack_minutes)}`}
                  </p>
                )}
                <div className="route-stop">
                  <span className="route-number" aria-hidden="true">
                    {i + 1}
                  </span>
                  <span className="route-time">{fmtTime(s.start_at)}</span>
                  <span>
                    <strong>{s.lead_name ?? 'Termin'}</strong>
                    <span className="muted block">{[s.postal_code, s.city].filter(Boolean).join(' ') || 'Ohne Adresse'}</span>
                  </span>
                </div>
                {hintsFor(s.appointment_id).map((h) => (
                  <p key={h.type} className={h.type === 'LATE_ARRIVAL' ? 'route-hint alert alert-error' : 'route-hint alert alert-warn'}>
                    <Icon name={hintIcons[h.type] ?? 'warning'} size={16} /> {h.message}
                  </p>
                ))}
              </li>
            ))}
          </ol>
          {route.attribution === 'GOOGLE_MAPS' && <GoogleMapsAttribution label="Fahrzeiten und Positionen:" />}
        </>
      )}
    </section>
  );
}
